import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box, Button, Chip, IconButton, InputAdornment, LinearProgress,
    MenuItem, Select, TextField, Tooltip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { confirmDialog } from "../../components/popups/ConfirmDialog";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../app/lang/langs";
import styles from "./VReference.module.css";
import { config } from "../../app/utils/config";
import { checkPermission } from "../../app/utils/helpers";
import VReferenceModal from "./VReferenceModal";
import MobileVReferenceView from "./mobile/MobileVReferenceView";
import {
    VREFERENCE_STATUSES, VREFERENCE_STATUS_COLORS, VREFERENCE_STATUS_FALLBACK_COLOR,
    deleteVReference, getAllVReferences, getVReferencePostes,
    searchVReferences, setQuery, setStatusFilter, updateVReferenceStatus
} from "./vreferenceSlice";

// Long enough that a normal typing burst is one request, short enough that the list feels
// live. The search hits a FULLTEXT index, so the cost is per-request overhead, not per-row.
const SEARCH_DEBOUNCE_MS = 300

// Matches the density of the PrimeReact DataTables the rest of the app uses, so a user
// moving between screens does not feel the row rhythm change.
const ROW_HEIGHT = 46
const HEADER_HEIGHT = 44

/**
 * The `sx` for a status pill. Shared by the read-only chip and the chips inside the status
 * editor so the two can never drift. Kept local rather than exported — the mobile twin is
 * imported by this file, so anything it imported back would be a cycle.
 */
const statusChipSx = (status) => ({
    ...(VREFERENCE_STATUS_COLORS[status] || VREFERENCE_STATUS_FALLBACK_COLOR),
    '& .MuiChip-label': { fontFamily: 'roboto', fontSize: 12 }
})

/**
 * V-Reference — the referral contact directory.
 *
 * Two access tiers share this screen (see VReferencesController): a `view` holder sees only
 * the contacts they added, an `edit` holder sees the whole directory. The distinction is NOT
 * re-derived here — the server already applied it when it filtered the rows and reports its
 * own conclusion as `canManageAll`. This component only uses that flag to decide which
 * buttons are worth rendering; every action is authorised again server-side.
 */
export function VReferenceView() {

    const dispatch = useDispatch()
    const debounceRef = useRef(null)

    const list = useSelector((state) => state.vreference.list)
    const canManageAll = useSelector((state) => state.vreference.canManageAll)
    const query = useSelector((state) => state.vreference.query)
    const statusFilter = useSelector((state) => state.vreference.statusFilter)
    const isMobile = useSelector((state) => state.settings.isMobile)
    const userId = useSelector((state) => state.login.userId)
    const lang = useSelector((state) => state.settings.lang)

    const [isSearching, setIsSearching] = useState(false)
    const [selectedContact, setSelectedContact] = useState(null)
    const [showModal, setShowModal] = useState(false)

    const canEdit = checkPermission(config.scopes.vreference, config.permissionTypes.edit)

    /**
     * Whether the HR-only fields (referral status, HR review note) exist for this user.
     *
     * Gated on the edit tier, NOT on row ownership — owning the row is deliberately not
     * enough. The recruitment status belongs to the hiring process, not to the person who
     * made the introduction: someone who referred a friend has no business reading
     * "interviewed, not retained" about them. The backend does not send these fields to
     * anyone else either, so this only decides whether to draw the controls.
     */
    const canSeeHrFields = canManageAll || canEdit

    /** Whether the current user may act on this specific row. Mirrors the backend rule. */
    const canManageRow = useCallback(
        // Loose == on purpose: created_by arrives as a string from the API and userId is a
        // number, the same reason checkPermission compares loosely.
        (row) => canManageAll || canEdit || row.created_by == userId,
        [canManageAll, canEdit, userId])

    const runSearch = useCallback(async (searchQuery, status) => {
        setIsSearching(true)
        if (searchQuery && searchQuery.trim()) {
            await dispatch(searchVReferences({ query: searchQuery, status: status || undefined }))
        } else {
            await dispatch(getAllVReferences({ status: status || undefined }))
        }
        setIsSearching(false)
    }, [dispatch])

    useEffect(() => {
        dispatch(getVReferencePostes())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => runSearch(query, statusFilter), SEARCH_DEBOUNCE_MS)
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
    }, [query, statusFilter, runSearch])

    const reload = () => runSearch(query, statusFilter)

    const onChangeStatus = async (row, status) => {
        const response = await dispatch(updateVReferenceStatus({ id: row.id, status }))
        if (response.payload && response.payload.responseData
            && response.payload.responseData.status === 1) {
            toast.success(Localize("VREFERENCE_STATUS_UPDATED", lang))
            reload()
        } else {
            toast.error(Localize("VREFERENCE_ACTION_FAILED", lang))
        }
    }

    const onDelete = (row) => {
        confirmDialog({
            message: Localize("VREFERENCE_DELETE_CONFIRM", lang)
                .replace("%name%", `${row.first_name} ${row.last_name}`),
            title: Localize("VREFERENCE_DELETE_TITLE", lang),
            danger: true,
            confirmLabel: Localize("YES", lang),
            cancelLabel: Localize("NO", lang),
            onConfirm: async () => {
                const response = await dispatch(deleteVReference({ id: row.id }))
                if (response.payload && response.payload.responseData
                    && response.payload.responseData.status === 1) {
                    toast.success(Localize("VREFERENCE_DELETED", lang))
                    reload()
                } else {
                    toast.error(Localize("VREFERENCE_ACTION_FAILED", lang))
                }
            }
        })
    }

    const openModal = (contact) => {
        setSelectedContact(contact)
        setShowModal(true)
    }

    /** The grid's own empty state — the default one-line MUI overlay reads as a broken table. */
    const NoRowsOverlay = useCallback(() => (
        <div className={styles.emptyState}>
            <PeopleOutlineIcon className={styles.emptyStateIcon} fontSize="large" />
            <Langs str="VREFERENCE_EMPTY" />
        </div>
    ), [])

    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: Localize("VREFERENCE_NAME", lang),
            flex: 1.2,
            minWidth: 180,
            valueGetter: (value, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim(),
            renderCell: (params) => (
                <span className={styles.nameCell} onClick={() => openModal(params.row)}>
                    {params.value}
                </span>
            )
        },
        {
            field: 'poste',
            headerName: Localize("VREFERENCE_POSTE", lang),
            flex: 1,
            minWidth: 150,
            // The referential label when the post was picked from `postes`, the typed one
            // when it was not. Exactly the fallback the schema was designed for.
            valueGetter: (value, row) => row.poste_name || row.poste_label || '-'
        },
        {
            field: 'company',
            headerName: Localize("VREFERENCE_COMPANY", lang),
            flex: 1,
            minWidth: 130,
            valueGetter: (value, row) => row.company || '-'
        },
        {
            field: 'email',
            headerName: Localize("VREFERENCE_EMAIL", lang),
            flex: 1.2,
            minWidth: 180,
            valueGetter: (value, row) => row.email || '-'
        },
        {
            field: 'phone',
            headerName: Localize("VREFERENCE_PHONE", lang),
            flex: 0.8,
            minWidth: 130,
            valueGetter: (value, row) => row.phone || '-'
        },
        // The status column exists ONLY for HR, and is spliced in rather than rendered blank:
        // a regular collab sees a table with no status column, not an empty one that invites
        // the question.
        ...(canSeeHrFields ? [{
            field: 'status',
            headerName: Localize("VREFERENCE_STATUS", lang),
            flex: 1,
            minWidth: 165,
            sortable: false,
            renderCell: (params) => (
                <Select
                    size="small"
                    variant="standard"
                    disableUnderline
                    value={params.row.status || 'new'}
                    onChange={(e) => onChangeStatus(params.row, e.target.value)}
                    className={styles.statusSelect}
                    MenuProps={{ classes: { paper: styles.vreference } }}
                >
                    {VREFERENCE_STATUSES.map(status => (
                        <MenuItem key={status} value={status}>
                            <Chip size="small"
                                className={styles.statusChip}
                                sx={statusChipSx(status)}
                                label={Localize(`VREFERENCE_STATUS_${status.toUpperCase()}`, lang)} />
                        </MenuItem>
                    ))}
                </Select>
            )
        }] : []),
        {
            field: 'created_by_name',
            headerName: Localize("VREFERENCE_REFERRED_BY", lang),
            flex: 1,
            minWidth: 150,
            valueGetter: (value, row) => row.created_by_name || '-'
        },
        {
            field: 'actions',
            headerName: '',
            width: 110,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                if (!canManageRow(params.row)) return null
                return (
                    <Box className={styles.rowActions}>
                        <Tooltip title={Localize("VREFERENCE_OPEN", lang)}>
                            <IconButton size="small" onClick={() => openModal(params.row)}>
                                <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={Localize("DELETE", lang)}>
                            <IconButton size="small" onClick={() => onDelete(params.row)}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [lang, canManageRow, canSeeHrFields])

    if (isMobile == 2 || isMobile == 3) {
        return <MobileVReferenceView />
    }

    return (
        <div className={styles.vreference}>

            <div className="topMenu">
                <Row>
                    <Col lg={7} className="d-flex align-items-center">
                        <span className="headerTitle">
                            <Langs str="VREFERENCE_TITLE" />
                        </span>
                        {/* Says out loud which of the two directories the user is looking at,
                            so a view-only user is not left wondering where everyone else's
                            contacts went. */}
                        <span className={styles.headerScope}>
                            <Langs str={(canManageAll || canEdit)
                                ? "VREFERENCE_SCOPE_ALL" : "VREFERENCE_SCOPE_MINE"} />
                        </span>
                    </Col>
                    <Col lg={5} className="d-flex justify-content-end align-items-center">
                        <Button
                            className={`${styles.pillButton} ${styles.pillButtonSmall}`}
                            startIcon={<PersonAddAlt1Icon />}
                            onClick={() => openModal(null)}
                        >
                            <Langs str="VREFERENCE_ADD" />
                        </Button>
                    </Col>
                </Row>
            </div>

            <div className={styles.bodyContainer}>
                <div className="bodyCard">

                    <div className={styles.toolbar}>
                        <TextField
                            className={styles.searchField}
                            size="small"
                            placeholder={Localize("VREFERENCE_SEARCH_PLACEHOLDER", lang)}
                            value={query}
                            onChange={(e) => dispatch(setQuery(e.target.value))}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title={Localize("VREFERENCE_SEARCH_HINT", lang)}>
                                            <DescriptionOutlinedIcon fontSize="small" color="action" />
                                        </Tooltip>
                                    </InputAdornment>
                                )
                            }}
                        />

                        {/* Filtering on a column you cannot see would reveal it by
                            elimination, so the control is HR-only too. The backend ignores
                            the filter for everyone else regardless. */}
                        {canSeeHrFields ? (
                            <Select
                                size="small"
                                displayEmpty
                                value={statusFilter}
                                onChange={(e) => dispatch(setStatusFilter(e.target.value))}
                                className={styles.statusFilter}
                                MenuProps={{ classes: { paper: styles.vreference } }}
                            >
                                <MenuItem value=""><Langs str="VREFERENCE_STATUS_ALL" /></MenuItem>
                                {VREFERENCE_STATUSES.map(status => (
                                    <MenuItem key={status} value={status}>
                                        <Langs str={`VREFERENCE_STATUS_${status.toUpperCase()}`} />
                                    </MenuItem>
                                ))}
                            </Select>
                        ) : null}
                    </div>

                    {isSearching
                        ? <LinearProgress className={styles.searchProgress} />
                        : <div className={styles.searchProgressPlaceholder} />}

                    <div className={styles.gridWrapper}>
                        <DataGrid
                            rows={list}
                            columns={columns}
                            getRowId={(row) => row.id}
                            disableRowSelectionOnClick
                            rowHeight={ROW_HEIGHT}
                            columnHeaderHeight={HEADER_HEIGHT}
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                            slots={{ noRowsOverlay: NoRowsOverlay }}
                            localeText={{ noRowsLabel: Localize("VREFERENCE_EMPTY", lang) }}
                            // The grid is the one place MUI's own chrome shows through, so the
                            // brand tokens are applied here rather than left to the default
                            // blue-and-Helvetica theme.
                            sx={{
                                border: 'none',
                                fontFamily: 'roboto',
                                fontSize: 14,
                                '--DataGrid-containerBackground': 'var(--lightGrey)',
                                '& .MuiDataGrid-columnHeader': {
                                    backgroundColor: 'var(--lightGrey)'
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontFamily: 'roboto-medium',
                                    fontSize: 14
                                },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid var(--lightGrey)'
                                },
                                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                                    outline: 'none'
                                },
                                '& .MuiDataGrid-row': {
                                    transition: 'background 0.2s ease'
                                },
                                '& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
                                    background: 'var(--orange-light-gradient)'
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    borderTop: '1px solid var(--grey)',
                                    fontFamily: 'roboto'
                                },
                                '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                    fontFamily: 'roboto',
                                    fontSize: 13
                                }
                            }}
                        />
                    </div>
                </div>
            </div>

            <VReferenceModal
                show={showModal}
                canSeeHrFields={canSeeHrFields}
                contact={selectedContact}
                onHide={() => { setShowModal(false); setSelectedContact(null) }}
                onSaved={() => { setShowModal(false); setSelectedContact(null); reload() }}
            />
        </div>
    )
}

export default VReferenceView
